import React, { useState } from 'react';
import { Plus, Search, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useAppStore } from '../contexts/AppStore';

export const StakeholdersPage: React.FC = () => {
  const { stakeholders } = useAppStore();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Filtrar stakeholders
  const filteredStakeholders = stakeholders.filter(stakeholder =>
    stakeholder.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stakeholder.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    stakeholder.sectors.some(sector => sector.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      'Empresa': 'bg-blue-100 text-blue-800',
      'Inversor': 'bg-green-100 text-green-800',
      'Investigador': 'bg-purple-100 text-purple-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Stakeholders (CRM)</h1>
          <p className="text-gray-600 mt-1">
            Gestión de relaciones con empresas, inversores e investigadores
          </p>
        </div>
        
        <Button
          variant="primary"
          className="flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Agregar Stakeholder</span>
        </Button>
      </div>
      
      {/* Barra de búsqueda */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Buscar por nombre, tipo o sector..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          <option value="">Todos los tipos</option>
          <option value="Empresa">Empresa</option>
          <option value="Inversor">Inversor</option>
          <option value="Investigador">Investigador</option>
        </select>
      </div>
      
      {/* Grid de stakeholders */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStakeholders.map((stakeholder) => (
          <Card key={stakeholder.id} className="hover:shadow-lg transition-shadow cursor-pointer">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {stakeholder.name}
                  </h3>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${getTypeColor(stakeholder.type)}`}>
                    {stakeholder.type}
                  </span>
                </div>
              </div>
              
              {/* Sectores */}
              <div>
                <p className="text-xs font-medium text-gray-600 mb-2">Sectores de Interés</p>
                <div className="flex flex-wrap gap-1">
                  {stakeholder.sectors.map((sector, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                    >
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Contacto */}
              <div className="space-y-2 pt-4 border-t border-gray-200">
                {stakeholder.email && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Mail size={16} className="text-gray-400" />
                    <span className="truncate">{stakeholder.email}</span>
                  </div>
                )}
                {stakeholder.phone && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Phone size={16} className="text-gray-400" />
                    <span>{stakeholder.phone}</span>
                  </div>
                )}
              </div>
              
              {/* Última interacción */}
              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  Última interacción: {format(new Date(stakeholder.lastInteraction), 'dd MMM yyyy', { locale: es })}
                </p>
              </div>
              
              {/* Acciones */}
              <div className="flex space-x-2">
                <Button variant="secondary" size="sm" className="flex-1">
                  Ver Detalles
                </Button>
                <Button variant="ghost" size="sm" className="flex-1">
                  Contactar
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
